import React, { useCallback, useEffect, useRef, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import {
  getDealerAdminPurchase,
  updateDealerAdminPurchase,
  getDealerAdminPurchaseTracking,
  getDealerAdminSailings,
  uploadDealerPurchaseMedia,
  removeDealerPurchaseMedia,
  getDealerAdminInvoicePdf,
  openPdf,
  formatUsd,
  DEALER_PURCHASE_STATUSES,
  DOCUMENT_CARRIERS,
} from "../../../services/dealerGarage";
import BillingPanel from "./BillingPanel";
import QuoteFields, { portName } from "./QuoteFields";
import VesselNameField, { likelySailing, shipFieldsFrom } from "./VesselNameField";
import { ShipTrackingView } from "../../GarageContent/ShipTracking";
import CustomerLink from "./CustomerLink";
import PanelFrame from "./PanelFrame";
import MoreMenu from "./MoreMenu";
import StaffNotesBox from "./StaffNotesBox";
import {
  formatDate, formatPhone, sinceLabel, vehicleLabel, customerLabel, customerRefOf, daysInStep, errorMessage, parseMoney, todayYmd,
  ageLabel, daysUntil,
} from "./dealerAdmin";

const SHIPPING_FIELDS = [
  ["shipName", "Vessel name", "text"],
  ["imo", "IMO (7 digits, for tracking)", "text"],
  ["pickupPort", "Loading port (Japan)", "text"],
  ["deliveryPort", "Arrival port (US)", "text"],
  ["estimatedPickupDate", "Departs", "date"],
  ["estimatedArrivalDate", "Arrives", "date"],
];

const TRACKABLE = ["Ship Booked", "In Transit", "Arrived at Port"];

// Steps where photos from our yard in Japan belong.
const YARD_STEPS = ["Transport to Warehouse", "In Storage", "Export Processing"];

// Through US customs: the paperwork can go out (customs needs the export
// certificate until then).
const CLEARED_STEPS = ["Ready for Pickup", "Delivery Scheduled", "Completed"];

// From the car's landing on there's no ETA to show.
const LANDED_STEPS = ["Arrived at Port", "Customs Clearance", ...CLEARED_STEPS, "Cancelled"];

const SOURCE_LABELS = { "current-inventory": "US inventory", auction: "Auction win", "dealer-stock": "Japan dealer stock" };

export const PaymentChip = ({ billing }) => {
  if (!billing) return null;
  const tone = {
    "Paid in full": styles.chipGood,
    "Balance due": styles.chipWarn,
    "Awaiting payment": styles.chipWarn,
    "Not invoiced": styles.chipMuted,
  }[billing.label] || styles.chipMuted;
  return (
    <span className={`${styles.chip} ${tone}`}>
      {billing.label}{billing.label === "Balance due" ? ` ${formatUsd(billing.balance, { cents: false })}` : ""}
    </span>
  );
};

// The car's photo, or its make's initials when the listing had none.
export const VehicleThumb = ({ record, className }) => {
  const v = (record && record.vehicle) || {};
  if (v.imageURL && /^https?:\/\//i.test(v.imageURL)) {
    return <img src={v.imageURL} alt="" className={className} loading="lazy" />;
  }
  return <div className={`${className} ${styles.thumbPlaceholder}`} aria-hidden="true">{(v.make || "?").slice(0, 3)}</div>;
};

// Connected dots, one per step of this car's plan (or an inspection's stages):
// done, current, still to come. Turns vertical on a phone unless `className`
// adds styles.progressCompact.
export const StepProgress = ({ plan, status, label = "Purchase steps", className = "" }) => {
  const at = plan.indexOf(status);
  return (
    <ol className={`${styles.progress} ${className}`} aria-label={label}>
      {plan.map((step, i) => {
        const state = i < at ? styles.progressDone : i === at ? styles.progressCurrent : "";
        return (
          <li key={step} className={`${styles.progressStep} ${state}`} aria-current={i === at ? "step" : undefined}>
            <span className={styles.progressDot}>{i < at ? "✓" : i + 1}</span>
            <span className={styles.progressLabel}>{step}</span>
          </li>
        );
      })}
    </ol>
  );
};

// A sidebar card of label / value facts (the empty ones left out), with an
// action beside the title (Edit, on the Step tab).
const FactCard = ({ title, rows, empty, action, children }) => {
  const shown = rows.filter(([, value]) => value);
  return (
    <div className={styles.sideCard}>
      <div className={styles.sideCardHead}>
        <h3>{title}</h3>
        {action}
      </div>
      {shown.length ? (
        <dl className={styles.factList}>
          {shown.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      ) : <p className={styles.hint}>{empty}</p>}
      {children}
    </div>
  );
};

const TABS = [
  { id: "step", label: "Step" },
  { id: "details", label: "Details" },
  // Only while the car is on a ship step: the customer has no map before or after.
  { id: "map", label: "Map" },
  { id: "billing", label: "Billing" },
  { id: "history", label: "History" },
];

const detailsFrom = (p) => ({
  price: p.price === null || p.price === undefined ? "" : String(p.price),
  purchaseDate: p.purchaseDate || "",
  vin: p.vin || "",
  notes: p.notes || "",
  quote: {
    quoteType: (p.quote && p.quote.quoteType) || "",
    port: (p.quote && p.quote.port) || "",
    includeTitle: !!(p.quote && p.quote.includeTitle),
  },
  shippingDetail: SHIPPING_FIELDS.reduce((acc, [key]) => {
    acc[key] = p.shippingDetail && p.shippingDetail[key] ? String(p.shippingDetail[key]) : "";
    return acc;
  }, {}),
  delivery: {
    method: (p.delivery && p.delivery.method) || "",
    address: (p.delivery && p.delivery.address) || "",
    scheduledDate: (p.delivery && p.delivery.scheduledDate) || "",
    notes: (p.delivery && p.delivery.notes) || "",
  },
  storage: {
    eligibleOn: (p.storage && p.storage.eligibleOn) || "",
    notes: (p.storage && p.storage.notes) || "",
  },
  documents: {
    carrier: (p.documents && p.documents.carrier) || "",
    trackingNumber: (p.documents && p.documents.trackingNumber) || "",
    mailedOn: (p.documents && p.documents.mailedOn) || "",
    contents: (p.documents && p.documents.contents) || "",
  },
  notifyDocuments: false,
});

/**
 * Everything about one dealer purchase in one place: move it to its next step
 * (with a note and an email to the customer), shipping and hand-off details,
 * its invoices and payments, and who did what when. The header holds the car,
 * who's buying it, its step, payment and ETA, and "Move to" the next step;
 * under it, what the sale is waiting on (a hand-off date, their payment, the
 * day it may leave storage). The Step tab previews what the customer reads
 * for the step, beside a summary of shipping, hand-off, storage, documents and
 * the staff notes.
 *
 * Props: purchaseId, onClose(), onChanged(purchase) - the board refreshes,
 *        initialTab - the tab to open on ("billing" for a sale just started),
 *        onOpenCustomer(ref) - the customer's name opens their page.
 */
const PurchasePanel = ({ purchaseId, onClose, onChanged, initialTab, onOpenCustomer }) => {
  const [purchase, setPurchase] = useState(null);
  const [details, setDetails] = useState(null);
  const [step, setStep] = useState({ status: "", statusDate: todayYmd(), statusNote: "", notify: true });
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState(undefined);
  // Why the last tracking lookup failed (a key or credit problem), straight
  // from the API.
  const [lookupError, setLookupError] = useState("");
  // The rest of the lookup: the ship's reported destination and ETA (there even
  // when it is out of range with no position), why a working lookup found no
  // position, and whether satellite lookups are on.
  const [trackInfo, setTrackInfo] = useState(null);
  const [tab, setTab] = useState(initialTab || "step");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  // An invoice or payment form open under Billing.
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  // Staff notes, as typed: filled on the first load and saved on their own,
  // so other saves (which reload the forms) never clear them.
  const [notes, setNotes] = useState(null);
  const stepNoteField = useRef(null);
  // The ships other cars went on, to fill this car's shipping from: fetched
  // the first time Details opens.
  const [sailings, setSailings] = useState(null);
  const detailsOpen = tab === "details";

  const apply = useCallback((p) => {
    setPurchase(p);
    setDetails(detailsFrom(p));
    setStep({ status: p.nextStep || p.status, statusDate: todayYmd(), statusNote: "", notify: true });
  }, []);

  const load = useCallback(() => {
    getDealerAdminPurchase(purchaseId)
      .then((res) => {
        if (res?.message === "Success") {
          apply(res.data);
          setNotes((n) => (n === null ? res.data.adminNotes || "" : n));
        } else {
          alert(errorMessage(res, "Could not load the purchase."));
        }
      })
      .catch(() => alert("Could not load the purchase."));
  }, [purchaseId, apply]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!detailsOpen || sailings) return;
    getDealerAdminSailings(purchaseId)
      .then((res) => setSailings(Array.isArray(res && res.data) ? res.data : []))
      .catch(() => setSailings([]));
  }, [detailsOpen, sailings, purchaseId]);

  const trackable = purchase && TRACKABLE.includes(purchase.status) && /^\d{7}$/.test((purchase.shippingDetail && purchase.shippingDetail.imo) || "");
  // The saved IMO, so correcting it looks the new ship up.
  const trackedImo = trackable ? purchase.shippingDetail.imo : "";
  useEffect(() => {
    if (!trackedImo) return;
    let active = true;
    setPosition(undefined);
    setLookupError("");
    setTrackInfo(null);
    getDealerAdminPurchaseTracking(purchaseId)
      .then((res) => {
        if (!active) return;
        setPosition(res?.message === "Success" ? res.data : null);
        setLookupError((res && res.lookupError) || "");
        setTrackInfo(res?.message === "Success"
          ? { voyage: res.voyage || null, note: res.lookupNote || "", satellite: res.satellite || null }
          : null);
      })
      .catch(() => {
        if (active) setPosition(null);
      });
    return () => { active = false; };
  }, [trackedImo, purchaseId]);

  const save = async (body, label) => {
    setBusy(true);
    try {
      const res = await updateDealerAdminPurchase(purchaseId, body);
      if (res?.message === "Purchase updated successfully") {
        apply(res.data);
        if (onChanged) onChanged(res.data);
        return true;
      }
      alert(errorMessage(res, `Could not save the ${label}.`));
      return false;
    } catch (err) {
      alert(`Could not save the ${label}.`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const res = await uploadDealerPurchaseMedia(purchaseId, files);
      if (res?.message === "Media uploaded") setPurchase((p) => ({ ...p, images: res.data.images, videos: res.data.videos }));
      else alert(errorMessage(res, "The upload failed."));
    } catch (err) {
      alert("The upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeMedia = async (url) => {
    if (!window.confirm("Remove this from the customer's garage? The file is deleted for good.")) return;
    const res = await removeDealerPurchaseMedia(purchaseId, url).catch(() => null);
    if (res?.message === "Media removed") {
      setPurchase((p) => ({ ...p, images: res.data.images, videos: res.data.videos }));
      if (res.fileDeleted === false) alert("Removed from the garage, but the file could not be deleted from storage, so its link still works.");
    } else {
      alert(errorMessage(res, "Could not remove it."));
    }
  };

  // Moves the car to `status` with what the step form says (date, note, email).
  const moveTo = (status) => {
    if (!status || status === purchase.status) {
      alert("Pick the step the car has moved to.");
      return;
    }
    if (status === "Cancelled" && !window.confirm("Cancel this purchase? The customer is told it has been cancelled.")) return;
    save({ status, statusDate: step.statusDate, statusNote: step.statusNote.trim(), notify: step.notify }, "step");
  };

  const saveStep = (e) => {
    e.preventDefault();
    moveTo(step.status);
  };

  // From More: the same as moving it to Cancelled, with the note asked here.
  const cancelSale = () => {
    const note = window.prompt("Cancel this sale? The customer is told it has been cancelled. Note to them (optional):");
    if (note === null) return;
    save({ status: "Cancelled", statusDate: todayYmd(), statusNote: note.trim(), notify: true }, "step");
  };

  // Only the notes are taken from the response, so unsaved Details edits stay.
  const saveNotes = async () => {
    setBusy(true);
    const res = await updateDealerAdminPurchase(purchaseId, { adminNotes: notes }).catch(() => null);
    setBusy(false);
    if (res?.message === "Purchase updated successfully") {
      setPurchase((p) => ({ ...p, adminNotes: res.data.adminNotes }));
      if (onChanged) onChanged(res.data);
    } else {
      alert(errorMessage(res, "Could not save the notes."));
    }
  };

  // The documents email again (the one "Email the customer the tracking
  // number" sends with a save), for the tracking already saved.
  const emailDocuments = async () => {
    const docs = purchase.documents || {};
    const to = (purchase.userId && purchase.userId.email) || "the customer";
    if (!window.confirm(`Email ${to} the tracking number for their documents (${docs.trackingNumber})?`)) return;
    setBusy(true);
    const res = await updateDealerAdminPurchase(purchaseId, { notifyDocuments: true }).catch(() => null);
    setBusy(false);
    if (res?.message !== "Purchase updated successfully") alert(errorMessage(res, "Could not send the email."));
    else if (res.documentsEmailed) alert("Emailed them the tracking number.");
    else alert("The email didn't go out. Their garage still shows the tracking.");
  };

  const openInvoicePdf = () => openPdf(() => getDealerAdminInvoicePdf(purchase.billing.openInvoiceId)).catch((e) => alert(e.message));

  const saveDetails = (e) => {
    e.preventDefault();
    const price = parseMoney(details.price);
    if (Number.isNaN(price)) {
      alert("Price must be a number.");
      return;
    }
    const imo = details.shippingDetail.imo.trim();
    if (imo && !/^\d{7}$/.test(imo)) {
      alert("The IMO is the ship's 7 digit number (on VesselFinder or the booking).");
      return;
    }
    if (details.notifyDocuments && !details.documents.trackingNumber.trim()) {
      alert("Add the tracking number to email it to the customer.");
      return;
    }
    // Paperwork goes out once the car is through US customs, and on a deal
    // where we do the title we keep the export certificate. Asked only when
    // the documents are first marked as mailed.
    const docs = details.documents;
    const before = purchase.documents || {};
    const newlyMailed = !!(docs.carrier || docs.trackingNumber.trim() || docs.mailedOn)
      && !(before.carrier || before.trackingNumber || before.mailedOn);
    if (newlyMailed && !CLEARED_STEPS.includes(purchase.status)
      && !window.confirm("This car hasn't cleared customs yet. Paperwork normally goes out after customs clearance - save it as mailed anyway?")) {
      return;
    }
    if (newlyMailed && details.quote.includeTitle && /export/i.test(docs.contents)
      && !window.confirm("This deal includes the US title, so we keep the export certificate to do the title. Mail it to the customer anyway?")) {
      return;
    }
    save({
      price,
      purchaseDate: details.purchaseDate,
      vin: details.vin.trim(),
      notes: details.notes.trim(),
      quote: { ...details.quote, quoteType: details.quote.quoteType || null },
      shippingDetail: { ...details.shippingDetail, imo },
      delivery: details.delivery,
      storage: details.storage,
      documents: { ...details.documents, trackingNumber: details.documents.trackingNumber.trim() },
      notifyDocuments: details.notifyDocuments,
    }, "details");
  };

  if (!purchase || !details) {
    return (
      <PanelFrame label="Purchase" onClose={onClose} header={<h2>Loading...</h2>} />
    );
  }

  const plan = purchase.plan || DEALER_PURCHASE_STATUSES;
  const setShip = (key, value) => setDetails((d) => ({ ...d, shippingDetail: { ...d.shippingDetail, [key]: value } }));
  const fillShip = (fields) => setDetails((d) => ({ ...d, shippingDetail: { ...d.shippingDetail, ...fields } }));
  const setDelivery = (key, value) => setDetails((d) => ({ ...d, delivery: { ...d.delivery, [key]: value } }));
  const setGroup = (group, key, value) => setDetails((d) => ({ ...d, [group]: { ...d[group], [key]: value } }));
  const customerChoice = purchase.delivery && purchase.delivery.requestedBy === "customer" ? purchase.delivery : null;
  const media = [...(purchase.images || []), ...(purchase.videos || [])];
  const u = purchase.userId && typeof purchase.userId === "object" ? purchase.userId : null;
  // A guest account staff opened gets no emails (the API drops them).
  const guestAccount = !!(u && u.isGuest);
  const days = daysInStep(purchase);
  const counts = { history: (purchase.statusHistory || []).length, billing: purchase.billing ? purchase.billing.invoiceCount : 0 };
  // The ship as the customer's garage shows it: the same lookup, without the
  // staff-only reasons it found no position.
  const customerTracking = position === undefined
    ? { status: "loading", data: null }
    : { status: trackInfo ? "ready" : "error", data: position, voyage: trackInfo && trackInfo.voyage };
  const live = purchase.status !== "Completed" && purchase.status !== "Cancelled";
  // Offered while the car has no ship: the one other cars to its port are on.
  const sameShip = live && purchase.source !== "current-inventory" && !details.shippingDetail.shipName.trim()
    ? likelySailing(sailings, details.quote.port)
    : null;
  const billing = purchase.billing || {};
  const ship = purchase.shippingDetail || {};
  const delivery = purchase.delivery || {};
  const storage = purchase.storage || {};
  const docs = purchase.documents || {};
  const eta = ship.estimatedArrivalDate && !LANDED_STEPS.includes(purchase.status) ? ship.estimatedArrivalDate : "";
  const notesDirty = notes !== null && notes !== (purchase.adminNotes || "");

  // Closing (×, the backdrop, Escape) asks first when something typed would be lost.
  const canClose = () => {
    const unsaved = [
      notesDirty && "staff notes",
      step.statusNote.trim() && "the note on the step",
      JSON.stringify(details) !== JSON.stringify(detailsFrom(purchase)) && "your changes under Details",
      invoiceOpen && "the invoice or payment you're entering",
    ].filter(Boolean);
    return !unsaved.length || window.confirm(`Close without saving ${unsaved.join(" and ")}?`);
  };

  // The header's button: on the Step tab it moves the car on with what the
  // form says (note, date, email); elsewhere it opens that form first.
  const moveToNext = () => {
    if (tab !== "step") {
      setTab("step");
      setStep((s) => ({ ...s, status: purchase.nextStep }));
      setTimeout(() => { if (stepNoteField.current) stepNoteField.current.focus(); }, 0);
      return;
    }
    moveTo(purchase.nextStep);
  };

  const header = (
    <>
      <div className={styles.headerRow}>
        <VehicleThumb record={purchase} className={styles.headerThumb} />
        <div className={styles.headerMain}>
          <h2>
            {purchase.vehicle && purchase.vehicle.url
              ? <a href={purchase.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>{vehicleLabel(purchase)}</a>
              : vehicleLabel(purchase)}
          </h2>
          <div className={styles.hint}>
            {/* Their page takes this panel's place, so ask about unsaved text first. */}
            <CustomerLink customer={customerRefOf(purchase)} onOpen={onOpenCustomer && ((ref) => canClose() && onOpenCustomer(ref))}>
              {customerLabel(purchase)}
            </CustomerLink>
            {u && u.phone && <> · <a href={`tel:${u.phone}`} className={classes.clickableCell}>{formatPhone(u.phone)}</a></>}
            {" · "}{SOURCE_LABELS[purchase.source] || SOURCE_LABELS["dealer-stock"]}
            {purchase.price ? ` · agreed ${formatUsd(purchase.price, { cents: false })}` : ""}
          </div>
          <div className={styles.headerChips}>
            <span className={`${styles.chip} ${purchase.status === "Cancelled" ? styles.chipBad : ""}`}>{purchase.status}</span>
            <PaymentChip billing={purchase.billing} />
            {eta && <span className={`${styles.chip} ${styles.chipMuted}`}>ETA {formatDate(eta)}</span>}
            {days !== null && live && (
              <span className={styles.hint}>{days === 0 ? "since today" : `${days} day${days === 1 ? "" : "s"} in this step`}</span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.headerActions}>
        {live && purchase.nextStep && purchase.nextStep !== purchase.status && (
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={moveToNext}>
            Move to: {purchase.nextStep}
          </button>
        )}
        <MoreMenu
          disabled={busy}
          items={[
            docs.trackingNumber && !guestAccount && { label: "Email them the documents' tracking", onClick: emailDocuments },
            billing.openInvoiceId && { label: "Open the unpaid invoice (PDF)", onClick: openInvoicePdf },
            live && { label: "Cancel the sale", onClick: cancelSale },
          ]}
        />
      </div>
    </>
  );

  const tabs = (
    <div className={styles.tabs} role="tablist">
      {TABS.filter((t) => t.id !== "map" || TRACKABLE.includes(purchase.status)).map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          className={`${styles.tab} ${tab === t.id ? styles.tabActive : ""}`}
          onClick={() => setTab(t.id)}
        >
          {t.label}
          {counts[t.id] ? <span className={styles.tabCount}>{counts[t.id]}</span> : null}
        </button>
      ))}
    </div>
  );

  // What the sale is waiting on: a date for the hand-off they asked for from
  // their garage, their payment, or the day the car may leave storage.
  let attention = null;
  if (live && customerChoice && !customerChoice.scheduledDate) {
    attention = (
      <div className={`${styles.attention} ${styles.attentionWarn}`}>
        <span className={styles.attentionText}>
          {customerChoice.method === "delivery" ? "They want it delivered" : "They'll pick it up at the port"}
          {customerChoice.requestedAt ? ` (told us ${formatDate(customerChoice.requestedAt)})` : ""}: set the date under Details → Hand-off.
        </span>
        {tab !== "details" && <button type="button" className={styles.btn} onClick={() => setTab("details")}>Open Details</button>}
      </div>
    );
  } else if (purchase.status === "Awaiting Payment") {
    attention = (
      <div className={`${styles.attention} ${styles.attentionWarn}`}>
        <span className={styles.attentionText}>
          {billing.openInvoiceSentAt
            ? `Waiting on their payment: the invoice went out ${formatDate(billing.openInvoiceSentAt)} (${ageLabel(billing.openInvoiceSentAt)}) and isn't paid yet.`
            : billing.invoiceCount
              ? "Waiting on their payment."
              : "Waiting on their payment: nothing is invoiced yet - send the car invoice from Billing."}
          {" "}The payment that clears it moves this to Order Confirmed and emails their order confirmation.
        </span>
        {tab !== "billing" && <button type="button" className={styles.btn} onClick={() => setTab("billing")}>Open Billing</button>}
      </div>
    );
  } else if (purchase.status === "In Storage" && storage.eligibleOn) {
    const left = daysUntil(storage.eligibleOn);
    attention = (
      <div className={`${styles.attention} ${left !== null && left <= 0 ? styles.attentionWarn : ""}`}>
        <span className={styles.attentionText}>
          {left !== null && left <= 0
            ? `Eligible to import since ${formatDate(storage.eligibleOn)}: move it to Export Processing.`
            : `In storage until ${formatDate(storage.eligibleOn)}, when it can be imported${left ? ` (in ${left} day${left === 1 ? "" : "s"})` : ""}.`}
        </span>
      </div>
    );
  }

  // What the customer reads for the step being picked: the email's opening,
  // the note, and what happens next (moving a sale off Awaiting Payment by
  // hand sends the order confirmation instead).
  const copy = (purchase.stepCopy || {})[step.status];
  const confirming = purchase.status === "Awaiting Payment" && step.status === "Order Confirmed";
  const emailing = step.notify && !guestAccount;
  const carrierLabel = (DOCUMENT_CARRIERS.find((c) => c.value === docs.carrier) || {}).label || docs.carrier;
  const editLink = <button type="button" className={styles.btnText} style={{ padding: 0 }} onClick={() => setTab("details")}>Edit</button>;

  const sidebar = (
    <>
      <FactCard
        title="Shipping"
        action={editLink}
        rows={[
          ["Vessel", [ship.shipName, ship.imo ? `IMO ${ship.imo}` : ""].filter(Boolean).join(" · ")],
          ["Route", [ship.pickupPort, ship.deliveryPort].filter(Boolean).join(" → ")],
          ["Departs", formatDate(ship.estimatedPickupDate)],
          ["Arrives", formatDate(ship.estimatedArrivalDate)],
        ]}
        empty="No ship yet."
      >
        {TRACKABLE.includes(purchase.status) && (
          <button type="button" className={styles.btnText} style={{ paddingLeft: 0 }} onClick={() => setTab("map")}>See it on the map</button>
        )}
      </FactCard>

      <FactCard
        title="Hand-off"
        action={editLink}
        rows={[
          ["Customer will", { pickup: "Pick up", delivery: "Have it delivered" }[delivery.method] || ""],
          [delivery.method === "delivery" ? "Delivery date" : "Ready from", formatDate(delivery.scheduledDate)],
          [delivery.method === "delivery" ? "Address" : "Location", delivery.address],
          ["Details", delivery.notes],
        ]}
        empty="Not decided yet."
      />

      {purchase.source !== "current-inventory" && (
        <FactCard
          title="Storage in Japan"
          action={editLink}
          rows={[["Eligible on", formatDate(storage.eligibleOn)], ["Notes", storage.notes]]}
          empty="No storage date (only for a car under 25 years old)."
        />
      )}

      <FactCard
        title="Documents mailed"
        action={editLink}
        rows={[
          ["Carrier", carrierLabel],
          ["Tracking", docs.trackingNumber],
          ["Mailed on", formatDate(docs.mailedOn)],
          ["Inside", docs.contents],
        ]}
        empty="Not mailed yet."
      />

      <div className={styles.sideCard}>
        <h3>Staff notes</h3>
        <StaffNotesBox
          id="purchase-notes"
          rows={4}
          value={notes || ""}
          saved={purchase.adminNotes}
          onChange={setNotes}
          onSave={saveNotes}
          busy={busy}
        />
      </div>
    </>
  );

  return (
    <PanelFrame
      label={vehicleLabel(purchase)}
      onClose={onClose}
      canClose={canClose}
      header={header}
      tabs={tabs}
      attention={attention}
      sidebar={tab === "step" ? sidebar : null}
    >
      {/* ---- Step ---- */}
      {tab === "step" && (
      <div>
        {purchase.status === "Cancelled" ? (
          <p><span className={`${styles.chip} ${styles.chipBad}`}>This purchase was cancelled</span></p>
        ) : (
          <StepProgress plan={plan} status={purchase.status} />
        )}
        {purchase.status === "Awaiting Payment" && (
          <p className={styles.hint}>Paid some other way? Move it to Order Confirmed below.</p>
        )}
        <form className={styles.panelSection} onSubmit={saveStep}>
          <h3>Move the car on</h3>
          <div className={styles.grid2}>
            <div className={classes.formGroup}>
              <label className={classes.formLabel} htmlFor="purchase-step">Move to</label>
              <select id="purchase-step" className={classes.formInput} value={step.status} onChange={(e) => setStep((s) => ({ ...s, status: e.target.value }))}>
                {purchase.nextStep && <option value={purchase.nextStep}>{purchase.nextStep} (next)</option>}
                {DEALER_PURCHASE_STATUSES.filter((s) => s !== purchase.nextStep).map((s) => (
                  <option key={s} value={s} disabled={s === purchase.status}>{s}{s === purchase.status ? " (current)" : ""}</option>
                ))}
              </select>
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel} htmlFor="purchase-step-date">When it happened</label>
              <input id="purchase-step-date" className={classes.formInput} type="date" value={step.statusDate} onChange={(e) => setStep((s) => ({ ...s, statusDate: e.target.value }))} />
            </div>
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel} htmlFor="purchase-step-note">Note to the customer (optional)</label>
            <textarea
              id="purchase-step-note"
              ref={stepNoteField}
              className={classes.formInput}
              rows={2}
              value={step.statusNote}
              placeholder="Shown on their timeline and in the email, e.g. 'Loaded at Yokohama this morning.'"
              onChange={(e) => setStep((s) => ({ ...s, statusNote: e.target.value }))}
            />
          </div>
          {guestAccount ? (
            <p className={styles.hint}>Guest account: no email goes to them until they sign up.</p>
          ) : (
            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={step.notify} onChange={(e) => setStep((s) => ({ ...s, notify: e.target.checked }))} />
              Email the customer about this step
            </label>
          )}
          {copy && step.status !== purchase.status && (
            <div className={styles.stepPreview}>
              <div className={styles.hint}>{emailing ? "The email and their garage say:" : "Their garage says:"}</div>
              <p>{confirming && emailing ? `Thank you for your order! ${copy.summary}` : copy.summary}</p>
              {step.statusNote.trim() && <p><strong>A note from our team:</strong> {step.statusNote.trim()}</p>}
              {copy.next && <p><strong>What happens next:</strong> {copy.next}</p>}
            </div>
          )}
          {TRACKABLE.includes(step.status) && !details.shippingDetail.imo && (
            <p className={styles.hint}>Add the vessel and its IMO under Details → Shipping so the customer can follow the ship.</p>
          )}
          {["Ready for Pickup", "Delivery Scheduled"].includes(step.status) && !details.delivery.address && (
            <p className={styles.hint}>Fill in the hand-off address and date under Details first - they go in the email.</p>
          )}
          {step.status === "In Storage" && !details.storage.eligibleOn && (
            <p className={styles.hint}>Add the date it becomes eligible to import under Details → Storage - the customer sees it.</p>
          )}
          <div className={classes.formButtons}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || step.status === purchase.status}>
              {busy ? "Saving..." : `Move to ${step.status}`}
            </button>
          </div>
        </form>

        {/* The customer told us from their garage how they want it handed over. */}
        {customerChoice && (
          <div className={styles.panelSection}>
            <h3>The customer's choice</h3>
            <p>
              <span className={`${styles.chip} ${styles.chipWarn}`}>{customerChoice.method === "delivery" ? "Wants it delivered" : "Will pick it up at the port"}</span>
              {" "}<span className={styles.hint}>{formatDate(customerChoice.requestedAt)}</span>
            </p>
            {customerChoice.method === "delivery" && customerChoice.address && <p>To: {customerChoice.address}</p>}
            {customerChoice.customerNotes && <p className={styles.hint}>Their note: {customerChoice.customerNotes}</p>}
            {!customerChoice.scheduledDate && (
              <p className={styles.hint}>
                {customerChoice.method === "delivery" ? "Get the carrier quote, then set" : "Set"} the date under Details → Hand-off and move the car to{" "}
                {customerChoice.method === "delivery" ? "Delivery Scheduled" : "Ready for Pickup"}.
              </p>
            )}
          </div>
        )}

        {/* Photos from our yard: the customer sees them in their garage. */}
        {(YARD_STEPS.includes(purchase.status) || media.length > 0) && (
          <div className={styles.panelSection}>
            <h3>Photos from our yard</h3>
            <div className={classes.formButtons} style={{ alignItems: "center" }}>
              <button type="button" className={styles.btn} disabled={uploading} onClick={() => fileInput.current && fileInput.current.click()}>
                {uploading ? "Uploading..." : "Upload photos / video"}
              </button>
              <input ref={fileInput} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => upload(e.target.files)} />
              <span className={styles.hint}>They show in the customer's garage straight away, and the next step email mentions them.</span>
            </div>
            {media.length > 0 && (
              <div className={styles.gallery}>
                {(purchase.images || []).map((url) => (
                  <div key={url} className={styles.galleryItem}>
                    <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt="At our yard" loading="lazy" /></a>
                    <button type="button" onClick={() => removeMedia(url)}>Remove</button>
                  </div>
                ))}
                {(purchase.videos || []).map((url) => (
                  <div key={url} className={styles.galleryItem}>
                    <video src={url} controls preload="metadata" />
                    <button type="button" onClick={() => removeMedia(url)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* ---- Details, shipping, hand-off ---- */}
      {tab === "details" && (
      <form onSubmit={saveDetails}>
        <div className={styles.panelSection}>
        <h3>Shipping</h3>
        {sameShip && (
          <div className={styles.attention}>
            <span className={styles.attentionText}>
              {sameShip.cars} other car{sameShip.cars === 1 ? " to " : "s to "}{portName(details.quote.port)}{sameShip.cars === 1 ? " is" : " are"} on
              {" "}<strong>{sameShip.shipName}</strong>
              {sameShip.estimatedPickupDate ? `, departing ${formatDate(sameShip.estimatedPickupDate)}` : ""}.
            </span>
            <button type="button" className={styles.btn} onClick={() => fillShip(shipFieldsFrom(sameShip))}>Use this ship</button>
          </div>
        )}
        <div className={styles.grid2}>
          {SHIPPING_FIELDS.map(([key, label, type]) => (key === "shipName" ? (
            <VesselNameField
              key={key}
              label={label}
              value={details.shippingDetail.shipName}
              imo={details.shippingDetail.imo}
              sailings={sailings}
              quotePort={details.quote.port}
              onChange={(value) => setShip(key, value)}
              onPick={fillShip}
            />
          ) : (
            <div key={key} className={classes.formGroup}>
              <label className={classes.formLabel}>{label}</label>
              <input className={classes.formInput} type={type} value={details.shippingDetail[key]} onChange={(e) => setShip(key, e.target.value)} />
            </div>
          )))}
        </div>
        {trackable && (
          <p className={styles.hint}>
            {position === undefined && "Looking up the ship..."}
            {position === null && (lookupError || (trackInfo && trackInfo.note) || "No position yet.")}
            {position === null && trackInfo && trackInfo.voyage && (
              <>
                {trackInfo.voyage.destination ? ` · heading for ${trackInfo.voyage.destination}` : ""}
                {trackInfo.voyage.eta ? ` · ETA ${formatDate(trackInfo.voyage.eta)}` : ""}
                {trackInfo.voyage.lastReportAt ? ` · last report ${sinceLabel(trackInfo.voyage.lastReportAt)}` : ""}
              </>
            )}
            {position && (
              <>
                {position.name || "Vessel"} at {position.latitude.toFixed(2)}, {position.longitude.toFixed(2)}
                {position.speed !== null && position.speed !== undefined ? ` · ${position.speed} kn` : ""}
                {position.destination ? ` · heading for ${position.destination}` : ""}
                {position.eta ? ` · ETA ${formatDate(position.eta)}` : ""}
                {position.reportedAt ? ` · reported ${sinceLabel(position.reportedAt)}${position.source === "satellite" ? " by satellite" : ""}` : ""}
                {lookupError ? ` · not refreshed: ${lookupError}` : (trackInfo && trackInfo.note ? ` · ${trackInfo.note}` : "")}
              </>
            )}
            {position !== undefined && (
              <>
                {" · "}
                <a href={`https://www.vesselfinder.com/vessels/details/${purchase.shippingDetail.imo}`} target="_blank" rel="noopener noreferrer">VesselFinder</a>
              </>
            )}
            {position !== undefined && trackInfo && trackInfo.satellite && (
              <>
                <br />
                {trackInfo.satellite.enabled
                  ? `Satellite lookups on: at most every ${trackInfo.satellite.everyHours} h per ship${trackInfo.satellite.lastCheckedAt ? `, last ${sinceLabel(trackInfo.satellite.lastCheckedAt)}` : ""}.`
                  : "Satellite lookups off: mid-ocean the last shore position shows until the ship nears land (TRACKING_SATELLITE=on turns them on)."}
              </>
            )}
          </p>
        )}

        </div>
        <div className={styles.panelSection}>
        <h3>Hand-off</h3>
        <div className={styles.grid2}>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Customer will</label>
            <select className={classes.formInput} value={details.delivery.method} onChange={(e) => setDelivery("method", e.target.value)}>
              <option value="">Not decided</option>
              <option value="pickup">Pick up</option>
              <option value="delivery">Have it delivered</option>
            </select>
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>{details.delivery.method === "delivery" ? "Delivery date" : "Ready from"}</label>
            <input className={classes.formInput} type="date" value={details.delivery.scheduledDate} onChange={(e) => setDelivery("scheduledDate", e.target.value)} />
          </div>
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>{details.delivery.method === "delivery" ? "Delivery address" : "Pickup location"}</label>
          <textarea className={classes.formInput} rows={2} value={details.delivery.address} onChange={(e) => setDelivery("address", e.target.value)} />
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Hand-off details for the customer</label>
          <input className={classes.formInput} value={details.delivery.notes} placeholder="Carrier, contact, what to bring" onChange={(e) => setDelivery("notes", e.target.value)} />
        </div>
        {customerChoice && customerChoice.customerNotes && (
          <p className={styles.hint}>The customer's note: {customerChoice.customerNotes}</p>
        )}

        </div>
        <div className={styles.panelSection}>
        <h3>Storage in Japan</h3>
        <div className={styles.grid2}>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Eligible to import on</label>
            <input className={classes.formInput} type="date" value={details.storage.eligibleOn} onChange={(e) => setGroup("storage", "eligibleOn", e.target.value)} />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Storage notes (staff)</label>
            <input className={classes.formInput} value={details.storage.notes} placeholder="Indoor / outdoor, monthly rate" onChange={(e) => setGroup("storage", "notes", e.target.value)} />
          </div>
        </div>
        <p className={styles.hint}>For a car under 25 years old: the date adds an "In Storage" step to the customer's tracker and tells them when it can ship.</p>
        </div>
        <div className={styles.panelSection}>
        <h3>Documents mailed</h3>
        <p className={styles.hint}>
          {details.quote.includeTitle
            ? "This deal includes the US title: we keep the export certificate to do the title, so what you mail is the title once it's issued."
            : "Mail the export certificate + translation once the car has cleared US customs: the customer needs it to title the car."}
        </p>
        <div className={styles.grid2}>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Carrier</label>
            <select className={classes.formInput} value={details.documents.carrier} onChange={(e) => setGroup("documents", "carrier", e.target.value)}>
              <option value="">Not mailed yet</option>
              {DOCUMENT_CARRIERS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Tracking number</label>
            <input className={classes.formInput} value={details.documents.trackingNumber} onChange={(e) => setGroup("documents", "trackingNumber", e.target.value)} />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Mailed on</label>
            <input className={classes.formInput} type="date" value={details.documents.mailedOn} onChange={(e) => setGroup("documents", "mailedOn", e.target.value)} />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>What's inside (the customer reads this)</label>
            <input className={classes.formInput} value={details.documents.contents} placeholder={details.quote.includeTitle ? "US title" : "Export certificate + translation, bill of sale"} onChange={(e) => setGroup("documents", "contents", e.target.value)} />
          </div>
        </div>
        {!guestAccount && (
          <label className={styles.checkboxRow}>
            <input type="checkbox" checked={details.notifyDocuments} onChange={(e) => setDetails((d) => ({ ...d, notifyDocuments: e.target.checked }))} />
            Email the customer the tracking number when I save
          </label>
        )}
        </div>
        <div className={styles.panelSection}>
        <h3>Sale</h3>
        <div className={styles.grid2}>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>{purchase.source === "auction" ? "Winning price (USD)" : "Agreed price (USD)"}</label>
            <input className={classes.formInput} value={details.price} inputMode="decimal" onChange={(e) => setDetails((d) => ({ ...d, price: e.target.value }))} />
            {purchase.source !== "auction" && (
              <small className={styles.hint}>What they pay for the car as quoted, before the inspection fee. New invoices add up to it.</small>
            )}
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>Purchase date</label>
            <input className={classes.formInput} type="date" value={details.purchaseDate} onChange={(e) => setDetails((d) => ({ ...d, purchaseDate: e.target.value }))} />
          </div>
          <div className={classes.formGroup}>
            <label className={classes.formLabel}>VIN / chassis no.</label>
            <input className={classes.formInput} value={details.vin} onChange={(e) => setDetails((d) => ({ ...d, vin: e.target.value }))} />
          </div>
          <QuoteFields
            quote={details.quote}
            inUs={purchase.source === "current-inventory"}
            onChange={(quote) => setDetails((d) => ({ ...d, quote }))}
          />
        </div>
        <small className={styles.hint}>Shipped or imported, the port and the title price new invoices.</small>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Note to customer (always shown in their garage)</label>
          <textarea className={classes.formInput} rows={2} value={details.notes} onChange={(e) => setDetails((d) => ({ ...d, notes: e.target.value }))} />
        </div>
        </div>
        <div className={classes.formButtons} style={{ alignItems: "center", flexWrap: "wrap" }}>
          <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{busy ? "Saving..." : "Save details"}</button>
          <span className={styles.hint}>Doesn't email the customer (unless you ticked the documents box); their garage shows the new values straight away.</span>
        </div>
      </form>
      )}

      {/* ---- Map: the ship as the customer's garage shows it ---- */}
      {tab === "map" && (
      <div className={styles.panelSection}>
        <h3>What the customer sees</h3>
        {trackable ? (
          <>
            <ShipTrackingView tracking={customerTracking} vesselName={purchase.shippingDetail.shipName} />
            <p className={styles.hint}>
              <a href={`https://www.vesselfinder.com/vessels/details/${purchase.shippingDetail.imo}`} target="_blank" rel="noopener noreferrer">Open in VesselFinder</a>
            </p>
          </>
        ) : (
          <p className={styles.hint}>Add the vessel and its IMO under Details → Shipping so the customer can follow the ship.</p>
        )}
      </div>
      )}

      {/* ---- Billing ---- */}
      {tab === "billing" && <BillingPanel scope={{ purchaseId }} defaultKind="vehicle" onChange={load} onEditingChange={setInvoiceOpen} />}

      {/* ---- History ---- */}
      {tab === "history" && (
      <div>
        <ul className={styles.history}>
          {[...(purchase.statusHistory || [])].reverse().map((h, i) => (
            // History entries have no id; position from the end is stable while open.
            <li key={`${h.status}-${h.at}-${i}`}>
              <span>{formatDate(h.at)}</span>
              <span>
                <strong>{h.status}</strong>
                {h.note ? ` - ${h.note}` : ""}
                <span className={styles.hint}>
                  {h.by && h.by.firstName ? ` · ${h.by.firstName}` : ""}
                  {h.notified ? " · emailed" : ""}
                </span>
              </span>
            </li>
          ))}
        </ul>
        {purchase.inspectionId && typeof purchase.inspectionId === "object" && (
          <p className={styles.hint}>
            From an inspection{purchase.inspectionId.report && purchase.inspectionId.report.grade ? ` graded ${purchase.inspectionId.report.grade}` : ""}.
          </p>
        )}
      </div>
      )}
    </PanelFrame>
  );
};

export default PurchasePanel;
